/**
 * "RINGING NOW" (ops #84 phase 4, `docs/plan-client-workplace.md` section 7): a line on every office
 * screen while a call is on the business line, "Ringing now: Rosa Alvarez, The Alvarez household",
 * with Open to the household's page, or to the enquiry an unknown caller's call opened, named by the
 * last four digits of the number until the office names it. A call the office placed reads "On the
 * line". Nothing at all is drawn while nobody is on the line.
 *
 * A POLL, AND ONLY WHILE SOMEBODY CAN SEE IT. There is no live push anywhere in ops, and a Durable
 * Object or a socket is a new Cloudflare resource the owner has not been asked for, so the banner
 * asks `GET /api/calls/live` on arriving and every eight seconds after, while the tab is visible: a
 * hidden tab asks nothing, and coming back to the front asks at once. Eight seconds because a ring
 * runs about twenty before voicemail; section 13 of the plan counts what a day of it costs, three
 * statements a poll, and `POLL_MS` is the one line that changes it. A poll is asked only after the one
 * before it has answered, so a slow answer never stacks a second behind it.
 *
 * FIXED AT THE FOOT OF THE GLASS, above the phone's bottom bar and at the right of a desk, rather than
 * at the top of the page: a pop that scrolls away with a long client page is a pop nobody sees.
 *
 * The office's alone: `App.tsx` hands it to the shell in the office branch and never in a cleaner's,
 * and the server refuses a cleaner the read, which is the control (binding rule 6).
 */
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { listLiveCalls, type LiveCall } from '../api.ts';

/** How far apart the polls are, while the tab is visible. Section 13 of the plan counts a day of it. */
export const POLL_MS = 8000;

export function RingingNow() {
  const [calls, setCalls] = useState<readonly LiveCall[]>([]);

  useEffect(() => {
    let alive = true;
    let asking = false;
    let timer: ReturnType<typeof setTimeout> | null = null;
    const seen = () => document.visibilityState === 'visible';
    const stop = () => {
      if (timer !== null) clearTimeout(timer);
      timer = null;
    };
    const ask = () => {
      stop();
      if (!alive || asking || !seen()) return;
      asking = true;
      listLiveCalls()
        .then(
          (answer) => {
            if (alive) setCalls(answer.calls);
          },
          () => {
            // A poll that failed draws nothing rather than a call that may have ended; the next
            // poll draws whatever is true then. A sign-in that lapsed is the root's to say.
            if (alive) setCalls([]);
          },
        )
        .finally(() => {
          asking = false;
          if (alive && seen()) timer = setTimeout(ask, POLL_MS);
        });
    };
    const look = () => (seen() ? ask() : stop());
    ask();
    document.addEventListener('visibilitychange', look);
    return () => {
      alive = false;
      stop();
      document.removeEventListener('visibilitychange', look);
    };
  }, []);

  return (
    <div
      role="status"
      aria-live="polite"
      className="pointer-events-none fixed inset-x-3 bottom-[calc(4.5rem+env(safe-area-inset-bottom))] z-30 flex flex-col gap-2 md:right-6 md:bottom-6 md:left-auto md:w-[26rem]"
    >
      {calls.map((call) => (
        <div
          key={call.callSid}
          className="pointer-events-auto flex items-center justify-between gap-3 rounded-xl border-l-4 border-gold bg-navy py-2 pr-2 pl-4 text-cream shadow-lg"
        >
          <p className="min-w-0 text-sm leading-snug">
            <span className="font-semibold">
              {call.direction === 'outbound' ? 'On the line:' : 'Ringing now:'}
            </span>{' '}
            {call.name}
            {call.kind === 'enquiry'
              ? ', an enquiry'
              : call.household && call.household !== call.name
                ? `, ${call.household}`
                : ''}
          </p>
          <Link
            to={call.open}
            aria-label={`Open ${call.name}`}
            className="flex min-h-11 shrink-0 items-center rounded-lg bg-gold px-4 text-sm font-semibold text-navy desk:min-h-9"
          >
            Open
          </Link>
        </div>
      ))}
    </div>
  );
}
